// Draft-related utility functions

export const getCurrentTeam = (draftedPlayers, teamCount) => {
  const totalPicks = Object.keys(draftedPlayers).length;
  const roundNumber = Math.floor(totalPicks / teamCount);
  const pickInRound = totalPicks % teamCount;
  return roundNumber % 2 === 0
    ? pickInRound
    : (teamCount - 1 - pickInRound);
};

export const isTeamsTurn = (teamIndex, draftedPlayers, teamCount) => {
  const totalPicks = Object.values(draftedPlayers).length;
  const roundNumber = Math.floor(totalPicks / teamCount);
  const pickInRound = totalPicks % teamCount;

  // In even rounds, teams pick 0 to n
  // In odd rounds, teams pick n to 0 (snake)
  if (roundNumber % 2 === 0) {
    return teamIndex === pickInRound;
  } else {
    return teamIndex === (teamCount - 1 - pickInRound);
  }
};

export const getPicksUntilNextTurn = (draftedPlayers, teamCount) => {
  const totalPicks = Object.keys(draftedPlayers).length;
  const roundNumber = Math.floor(totalPicks / teamCount);
  const currentTeam = getCurrentTeam(draftedPlayers, teamCount) + 1;

  if (roundNumber % 2 === 0) {
    // Going in increasing order (0 to N-1)
    return (teamCount * 2) - (currentTeam * 2) + 1;
  } else {
    // Going in decreasing order (N-1 to 0)
    return (currentTeam * 2) - 1;
  }
};

// Counts open slots from the drafted field ids (`team-POS-index`) rather than
// inferring each player's position from the player lists: a player can appear
// in multiple lists (e.g. TE also listed as WR), so only the slot they actually
// fill says which count to decrement.
export const getRemainingCounts = (config, draftedPlayers) => {
  const filled = {};
  Object.keys(draftedPlayers).forEach(fieldId => {
    const pos = fieldId.split('-')[1];
    filled[pos] = (filled[pos] || 0) + 1;
  });

  const counts = {};
  let total = 0;
  for (const pos in config.positions) {
    counts[pos] = config.positions[pos] * config.teamCount - (filled[pos] || 0);
    total += counts[pos];
  }

  counts.TOTAL = total;
  return counts;
};

export const buildOpenSlotsTable = (config, draftedPlayers) => {
  const table = Array(config.teamCount).fill().map(() => ({}));

  // Initialize counts for each position for each team
  for (let teamIndex = 0; teamIndex < config.teamCount; teamIndex++) {
    Object.entries(config.positions).forEach(([pos, totalSlots]) => {
      let filledCount = 0;
      Object.entries(draftedPlayers).forEach(([fieldId, _]) => {
        if (fieldId.startsWith(`${teamIndex}-${pos}-`)) {
          filledCount++;
        }
      });
      table[teamIndex][pos] = totalSlots - filledCount;
    });
  }
  return table;
};

// The remaining pool is the full dataset minus the players already taken.
// Persisting it (and a copy per undo step) bloats saved drafts past the API's
// payload limit, so we drop it on save and rebuild it here on load.
export const computeAvailablePlayers = (customPlayerData, selectedNames) => {
  const selected = new Set(selectedNames || []);
  const available = {};
  Object.entries(customPlayerData || {}).forEach(([position, players]) => {
    available[position] = players.filter(p => !selected.has(p.name));
  });
  return available;
};

// Slim undo/redo entries for persistence: keep only what can't be recomputed,
// and store the selected set as an array so it survives JSON serialization.
export const slimHistoryForSave = (entries) =>
  (entries || []).map(entry => ({
    draftedPlayers: entry.draftedPlayers,
    selectedPlayers: Array.from(entry.selectedPlayers || [])
  }));

// Rebuild in-memory undo/redo entries (Set + reconstructed pool) from the slim
// persisted form. Tolerates legacy entries whose selectedPlayers isn't an array.
export const hydrateHistoryForLoad = (entries, customPlayerData) =>
  (entries || []).map(entry => {
    const selectedNames = Array.isArray(entry.selectedPlayers) ? entry.selectedPlayers : [];
    return {
      draftedPlayers: entry.draftedPlayers,
      selectedPlayers: new Set(selectedNames),
      availablePlayers: computeAvailablePlayers(customPlayerData, selectedNames)
    };
  });

// Build the slim, JSON-safe payload that gets persisted. The full dataset lives
// in customPlayerData; the working pool and per-entry pools are dropped because
// they are recomputable. availablePlayers is kept only as a fallback when the
// full dataset is unavailable (legacy drafts loaded without it), so a re-save
// never strips all player data.
export const buildDraftState = ({
  config,
  draftedPlayers,
  selectedPlayers,
  draftHistory,
  redoStack,
  customPlayerData,
  availablePlayers
}) => ({
  config,
  draftedPlayers,
  selectedPlayers: Array.from(selectedPlayers || []),
  draftHistory: slimHistoryForSave(draftHistory),
  redoStack: slimHistoryForSave(redoStack),
  customPlayerData: customPlayerData || null,
  ...(customPlayerData ? {} : { availablePlayers })
});

// Turn a persisted draft back into in-memory state. Rebuilds the pool from the
// full dataset when present, else uses a stored pool. Returns { valid: false }
// when the record has no player data at all.
export const reconstructDraft = (loadedDraft) => {
  const fullDataset = loadedDraft.customPlayerData || loadedDraft.playerData;
  const storedAvailable = loadedDraft.availablePlayers;

  if (!fullDataset && !storedAvailable) {
    return { valid: false };
  }

  const selectedPlayersArray = loadedDraft.selectedPlayers || [];
  const reconstructionSource = fullDataset || storedAvailable;

  return {
    valid: true,
    customPlayerData: fullDataset || null,
    availablePlayers: fullDataset
      ? computeAvailablePlayers(fullDataset, selectedPlayersArray)
      : storedAvailable,
    config: loadedDraft.config,
    draftedPlayers: loadedDraft.draftedPlayers,
    selectedPlayers: new Set(selectedPlayersArray),
    draftHistory: hydrateHistoryForLoad(loadedDraft.draftHistory, reconstructionSource),
    redoStack: hydrateHistoryForLoad(loadedDraft.redoStack, reconstructionSource)
  };
};

export const debounce = (func, wait) => {
  let timeout;
  const executedFunction = function (...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
  executedFunction.cancel = () => clearTimeout(timeout);
  return executedFunction;
};
